import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Card, CardContent, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, MenuItem, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, InputAdornment, Tooltip
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon,
  TimerOff as ExtendIcon, SwapHoriz as ChangePlanIcon,
  Pause as SuspendIcon, PlayArrow as ReactivateIcon,
  TrendingUp as MrrIcon, People as PeopleIcon,
  Warning as WarningIcon, CheckCircle as ActiveIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../../config';

const estadoColors = {
  TRIAL: { bg: '#FFF7ED', color: '#F59E0B' },
  ACTIVA: { bg: '#ECFDF5', color: '#10B981' },
  VENCIDA: { bg: '#FEF2F2', color: '#EF4444' },
  CANCELADA: { bg: '#F5F5F5', color: '#9E9E9E' },
  SUSPENDIDA: { bg: '#FFF3E0', color: '#FF9800' },
};

function MetricCard({ icon, label, value, sub, color }) {
  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${color}30` }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ bgcolor: `${color}15`, borderRadius: 2, p: 1.5, display: 'flex' }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function GestionSuscripciones() {
  const [resumen, setResumen] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [planes, setPlanes] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialogs
  const [extendDialog, setExtendDialog] = useState({ open: false, empresa: null });
  const [extendDias, setExtendDias] = useState(15);
  const [extendMotivo, setExtendMotivo] = useState('');

  const [planDialog, setPlanDialog] = useState({ open: false, empresa: null });
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedPeriodo, setSelectedPeriodo] = useState('MENSUAL');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroEstado) params.append('estado', filtroEstado);
      if (busqueda) params.append('search', busqueda);

      const [resumenRes, empresasRes, planesRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/v1/suscripciones/admin/resumen`),
        axios.get(`${config.API_BASE_URL}/api/v1/suscripciones/admin/empresas?${params}`),
        axios.get(`${config.API_BASE_URL}/api/v1/suscripciones/planes`),
      ]);

      if (resumenRes.data?.success) setResumen(resumenRes.data.data);
      if (empresasRes.data?.success) setEmpresas(empresasRes.data.data);
      if (planesRes.data?.success) setPlanes(planesRes.data.data);
    } catch (e) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, busqueda]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (action, body) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/v1/suscripciones/admin/${action}`, body);
      setSuccess(res.data?.mensaje || 'Acción completada');
      fetchData();
    } catch (e) {
      setError(e.response?.data?.error || 'Error en la acción');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = () => {
    handleAction('extender-trial', {
      id_empresa: extendDialog.empresa.id_empresa,
      dias: extendDias,
      motivo: extendMotivo,
    });
    setExtendDialog({ open: false, empresa: null });
  };

  const handleChangePlan = () => {
    handleAction('cambiar-plan', {
      id_empresa: planDialog.empresa.id_empresa,
      id_plan: selectedPlan,
      tipo_periodo: selectedPeriodo,
    });
    setPlanDialog({ open: false, empresa: null });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="#1E2783">Suscripciones</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchData} variant="outlined">Actualizar</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Métricas */}
      {resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard icon={<PeopleIcon />} label="En trial" value={resumen.empresas_trial} sub={`${resumen.trial_por_vencer} por vencer esta semana`} color="#F59E0B" />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard icon={<ActiveIcon />} label="Pagando" value={resumen.empresas_pagando} sub={`MRR: $${resumen.mrr_usd}`} color="#10B981" />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard icon={<WarningIcon />} label="Vencidas" value={resumen.empresas_vencidas} color="#EF4444" />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard icon={<MrrIcon />} label="Conversión" value={`${resumen.tasa_conversion}%`} sub="Trial → Pago" color="#1E2783" />
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Buscar empresa..."
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 250 }}
        />
        <TextField
          select size="small" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
          sx={{ minWidth: 150 }} label="Estado"
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="TRIAL">Trial</MenuItem>
          <MenuItem value="ACTIVA">Activa</MenuItem>
          <MenuItem value="VENCIDA">Vencida</MenuItem>
          <MenuItem value="SUSPENDIDA">Suspendida</MenuItem>
          <MenuItem value="CANCELADA">Cancelada</MenuItem>
        </TextField>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 700 }}>Empresa</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Días rest.</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Servicios</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Técnicos</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {empresas.map(e => {
              const sc = estadoColors[e.estado_suscripcion] || { bg: '#F5F5F5', color: '#999' };
              return (
                <TableRow key={e.id_empresa} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{e.nombre_empresa}</Typography>
                    <Typography variant="caption" color="text.secondary">{e.email_empresa}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={e.nombre_plan || e.codigo_plan || 'Sin plan'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={e.estado_suscripcion || 'N/A'} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    {e.dias_restantes !== null ? (
                      <Typography variant="body2" color={e.dias_restantes <= 7 ? 'error' : 'text.primary'} fontWeight={600}>
                        {e.dias_restantes}d
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {e.servicios_consumidos_mes}/{e.max_servicios_mes || '∞'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {e.tecnicos_activos}/{e.max_tecnicos || '∞'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {(e.estado_suscripcion === 'TRIAL' || e.estado_suscripcion === 'VENCIDA') && (
                        <Tooltip title="Extender trial">
                          <IconButton size="small" color="warning" onClick={() => setExtendDialog({ open: true, empresa: e })}>
                            <ExtendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Cambiar plan">
                        <IconButton size="small" color="primary" onClick={() => { setPlanDialog({ open: true, empresa: e }); setSelectedPlan(''); }}>
                          <ChangePlanIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {e.estado_suscripcion === 'ACTIVA' || e.estado_suscripcion === 'TRIAL' ? (
                        <Tooltip title="Suspender">
                          <IconButton size="small" color="error" onClick={() => handleAction('suspender', { id_empresa: e.id_empresa })}>
                            <SuspendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reactivar">
                          <IconButton size="small" color="success" onClick={() => handleAction('reactivar', { id_empresa: e.id_empresa })}>
                            <ReactivateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {empresas.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay empresas con suscripción
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Extender Trial */}
      <Dialog open={extendDialog.open} onClose={() => setExtendDialog({ open: false, empresa: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Extender Trial — {extendDialog.empresa?.nombre_empresa}</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="Días a extender" value={extendDias}
            onChange={(e) => setExtendDias(parseInt(e.target.value))}
            sx={{ mt: 2, mb: 2 }}
          >
            <MenuItem value={7}>7 días</MenuItem>
            <MenuItem value={15}>15 días</MenuItem>
            <MenuItem value={30}>30 días</MenuItem>
            <MenuItem value={60}>60 días</MenuItem>
          </TextField>
          <TextField
            fullWidth label="Motivo (opcional)" value={extendMotivo}
            onChange={(e) => setExtendMotivo(e.target.value)}
            placeholder="Ej: Cliente potencial, necesita más tiempo de evaluación"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog({ open: false, empresa: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleExtend} disabled={actionLoading}
            sx={{ bgcolor: '#F59E0B', '&:hover': { bgcolor: '#D97706' } }}>
            {actionLoading ? 'Extendiendo...' : `Extender ${extendDias} días`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Cambiar Plan */}
      <Dialog open={planDialog.open} onClose={() => setPlanDialog({ open: false, empresa: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Cambiar Plan — {planDialog.empresa?.nombre_empresa}</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="Plan" value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          >
            {planes.filter(p => p.codigo_plan !== 'FREE').map(p => (
              <MenuItem key={p.id_plan} value={p.id_plan}>
                {p.nombre} — ${p.precio_mensual_usd}/mes
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select fullWidth label="Período" value={selectedPeriodo}
            onChange={(e) => setSelectedPeriodo(e.target.value)}
          >
            <MenuItem value="MENSUAL">Mensual</MenuItem>
            <MenuItem value="ANUAL">Anual</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog({ open: false, empresa: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleChangePlan} disabled={actionLoading || !selectedPlan}
            sx={{ bgcolor: '#1E2783' }}>
            {actionLoading ? 'Cambiando...' : 'Asignar plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
